import { useState } from "react";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Compact "+ Add flavor" strip. Lets any logged-in user with permission
 * (admin / kitchen employee / franchisee) create a new flavor row for an
 * addable category (macaron, macaron_seasonal, gelato_pan/pint/pop) at
 * their own scoped location. The backend enforces the role + category
 * allow-list; this component just posts.
 *
 *   category          — e.g. "macaron"
 *   categoryLabel     — e.g. "Macarons"
 *   locationId        — optional (admin uses it to pick a target kitchen;
 *                        kitchen employees + franchisees are auto-scoped
 *                        server-side so this is ignored for them)
 *   initialStock      — initial on-hand to seed (default 0; useful for
 *                        franchisee adds where they already have units)
 *   onSaved(item)     — called with the created inventory item
 */
const BLISTER_CATEGORIES = new Set(["macaron", "macaron_seasonal"]);
const FULL_TRAY = 70;

export default function FlavorQuickAdd({
  category,
  categoryLabel,
  locationId,
  onSaved,
  variant = "section",   // "section" (bordered strip) | "row" (table row)
  initialStock = 0,
}) {
  const [name, setName] = useState("");
  const [entry, setEntry] = useState({});
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [premium, setPremium] = useState(false);
  const isBlister = BLISTER_CATEGORIES.has(category);
  const computedStock = isBlister
    ? (Number(entry.full) || 0) * FULL_TRAY + Math.min(70, Number(entry.partial) || 0)
    : (Number(qty) || 0);
  const totalStock = computedStock > 0 ? computedStock : initialStock;

  const save = async () => {
    if (!name.trim()) { toast.error("Type a flavor name first"); return; }
    setBusy(true);
    try {
      const packSize = isBlister ? 70 : 1;
      const unit = isBlister ? "box (70 pcs)" : "pan";
      const body = {
        name: name.trim(), flavor: name.trim(), category,
        unit, pack_size: packSize, deduction_qty: 1,
        low_stock_threshold: 5, current_stock: totalStock, price: 0, premium, available: true,
      };
      if (locationId) body.location_id = locationId;
      const { data: created } = await api.post("/inventory", body);
      toast.success(`${name.trim()} added${totalStock > 0 ? ` — ${totalStock} on hand` : ""}`);
      setName(""); setEntry({}); setQty("");
      onSaved?.(created);
    } catch (e) {
      toast.error("Couldn't add flavor", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const inputFields = isBlister ? (
    <>
      <input
        type="number" min="0" inputMode="numeric"
        value={entry.full ?? ""}
        onChange={(e) => setEntry((p) => ({ ...p, full: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
        placeholder="Full"
        title="Full boxes (× 70)"
        data-testid={`flavor-add-full-${category}`}
        className="h-9 w-16 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
      />
      <span className="text-[10px] text-muted-foreground">×70+</span>
      <input
        type="number" min="0" max="70" inputMode="numeric"
        value={entry.partial ?? ""}
        onChange={(e) => {
          const raw = e.target.value === "" ? "" : Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
          setEntry((p) => ({ ...p, partial: raw }));
        }}
        placeholder="Partial"
        title="Partial (0–70)"
        data-testid={`flavor-add-partial-${category}`}
        className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
      />
      <span className="text-[10px] text-muted-foreground tabular-nums">= {computedStock}</span>
    </>
  ) : (
    <input
      type="number" min="0" inputMode="numeric"
      value={qty}
      onChange={(e) => setQty(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))}
      placeholder="Count"
      data-testid={`flavor-add-qty-${category}`}
      className="h-9 w-24 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
    />
  );

  return (
    <div
      className={
        variant === "section"
          ? "mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-border bg-secondary/30 px-3 py-2"
          : "flex flex-wrap items-center gap-2"
      }
      data-testid={`flavor-add-strip-${category}`}
    >
      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={`Add ${categoryLabel} flavor…`}
        data-testid={`flavor-add-name-${category}`}
        className="h-9 flex-1 min-w-[10rem] max-w-xs rounded-lg border border-input bg-card px-3 text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      {inputFields}
      <PremiumToggle value={premium} onChange={setPremium} />
      <button
        onClick={save}
        disabled={busy || !name.trim()}
        data-testid={`flavor-add-save-${category}`}
        className="h-9 px-3 rounded-lg bg-foreground text-background text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-40"
      >
        <Save className="h-3.5 w-3.5" /> Add
      </button>
    </div>
  );
}

export const FLAVOR_ADDABLE_CATEGORIES = new Set([
  "macaron", "macaron_seasonal", "gelato_pan", "gelato_pint", "gelato_pop",
]);

// Admin / kitchen choose Standard vs Premium price when adding a flavor.
export function PremiumToggle({ value, onChange, allowWarehouse = false }) {
  const { user } = useAuth();
  if (!(user?.role === "admin" || user?.role === "employee" || (allowWarehouse && user?.warehouse))) return null;
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="premium-toggle">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
      Premium
    </label>
  );
}
